import type { MenuItem } from '@/types'
import type { MenuProps } from 'antd'
import React from 'react'
import {
  DashboardOutlined,
  UserOutlined,
  TeamOutlined,
  MenuOutlined,
  SafetyOutlined,
  SettingOutlined,
  ThunderboltOutlined,
  UserAddOutlined,
  ShoppingOutlined,
  BankOutlined,
  FileTextOutlined,
  WalletOutlined,
} from '@ant-design/icons'

// 根据权限过滤菜单
export const filterMenusByPermission = (menus: MenuItem[], permissions: string[]): MenuItem[] => {
  return menus
    .filter((menu) => {
      // 如果没有设置权限，则显示
      if (!menu.permission) {
        return true
      }
      // 检查是否有权限
      return permissions.includes(menu.permission)
    })
    .map((menu) => {
      if (menu.children) {
        return {
          ...menu,
          children: filterMenusByPermission(menu.children, permissions),
        }
      }
      return menu
    })
    .filter((menu) => {
      // 如果父菜单有子菜单，但子菜单都被过滤掉了，则隐藏父菜单
      if (menu.children && menu.children.length === 0) {
        return false
      }
      return true
    })
}

// 图标映射
const getIcon = (iconName: string): React.ReactNode => {
  switch (iconName) {
    case 'DashboardOutlined':
      return React.createElement(DashboardOutlined)
    case 'UserOutlined':
      return React.createElement(UserOutlined)
    case 'TeamOutlined':
      return React.createElement(TeamOutlined)
    case 'MenuOutlined':
      return React.createElement(MenuOutlined)
    case 'SafetyOutlined':
      return React.createElement(SafetyOutlined)
    case 'SettingOutlined':
      return React.createElement(SettingOutlined)
    case 'ThunderboltOutlined':
      return React.createElement(ThunderboltOutlined)
    case 'UserAddOutlined':
      return React.createElement(UserAddOutlined)
    case 'ShoppingOutlined':
      return React.createElement(ShoppingOutlined)
    case 'BankOutlined':
      return React.createElement(BankOutlined)
    case 'FileTextOutlined':
      return React.createElement(FileTextOutlined)
    case 'WalletOutlined':
      return React.createElement(WalletOutlined)
    default:
      return null
  }
}

// 生成Antd菜单项
export const generateMenuItems = (
  menus: MenuItem[],
  navigate: (path: string) => void
): MenuProps['items'] => {
  return menus.map((menu) => {
    const item: any = {
      key: menu.id,
      icon: menu.icon ? getIcon(menu.icon) : null,
      label: menu.title,
    }

    if (menu.children && menu.children.length > 0) {
      item.children = generateMenuItems(menu.children, navigate)
    } else if (menu.path) {
      item.onClick = () => navigate(menu.path!)
    }

    return item
  })
}

// 根据路径查找菜单
export const findMenuByPath = (menus: MenuItem[], path: string): MenuItem | null => {
  for (const menu of menus) {
    if (menu.path === path) {
      return menu
    }
    if (menu.children) {
      const found = findMenuByPath(menu.children, path)
      if (found) {
        return found
      }
    }
  }
  return null
}

// 根据ID查找菜单
export const findMenuById = (menus: MenuItem[], id: string): MenuItem | null => {
  for (const menu of menus) {
    if (menu.id === id) {
      return menu
    }
    if (menu.children) {
      const found = findMenuById(menu.children, id)
      if (found) {
        return found
      }
    }
  }
  return null
}

// 获取面包屑导航
export const getBreadcrumbs = (menus: MenuItem[], path: string): Array<{ title: string; path: string }> => {
  const breadcrumbs: Array<{ title: string; path: string }> = []

  const findPath = (menuList: MenuItem[], targetPath: string, parentPath: string[] = []): boolean => {
    for (const menu of menuList) {
      const currentPath = [...parentPath, menu.title]
      
      if (menu.path === targetPath) {
        breadcrumbs.push(...currentPath.map((title, index) => ({
          title,
          path: parentPath.slice(0, index + 1).join('/'),
        })))
        return true
      }
      
      if (menu.children) {
        if (findPath(menu.children, targetPath, currentPath)) {
          return true
        }
      }
    }
    return false
  }

  findPath(menus, path)
  return breadcrumbs
}

// 获取所有菜单路径
export const getAllMenuPaths = (menus: MenuItem[]): string[] => {
  const paths: string[] = []
  
  const traverse = (menuList: MenuItem[]) => {
    for (const menu of menuList) {
      if (menu.path) {
        paths.push(menu.path)
      }
      if (menu.children) {
        traverse(menu.children)
      }
    }
  }
  
  traverse(menus)
  return paths
}

/** 本地缓存的菜单是否缺少 Web3 等新菜单（用于 Redux Persist 过期数据自愈） */
export const isMenuTreeOutdated = (menus: MenuItem[]): boolean => {
  if (!menus?.length) return true
  return (
    findMenuByPath(menus, '/web3/lottery') === null ||
    findMenuByPath(menus, '/web3/liren/overview') === null ||
    findMenuByPath(menus, '/figma/chaiyao-home') === null
  )
}

// 检查是否有菜单权限
export const hasMenuPermission = (menus: MenuItem[], path: string, permissions: string[]): boolean => {
  const menu = findMenuByPath(menus, path)
  if (!menu) {
    return false
  }
  
  if (!menu.permission) {
    return true
  }
  
  return permissions.includes(menu.permission)
}
